import { useCachedResource } from '@shared/hooks/useCachedResource';
import { api_ } from '@shared/plugIn/axios';
import { kstDayEndISO, kstDayStartISO } from '@shared/utils/kstTime.util';

import { BugoRegionStatResponse, BugoRegionStatRow } from './adminBugoRegion.types';

/**
 * 지역별 통계 조회 — GET /admin/fevent/stats/region (docs/admin-bugo-region-stat-spec.md).
 *
 * 서버가 (상조·장례식장·월)로 접은 행만 주므로 응답이 작다(반기에 수천 행).
 * 부고 탭의 원자료 조회와 별개 요청이라 캐시를 태운다 — 섹션을 접었다 펴거나 기간을
 * 되돌려도 재조회가 없다. `enabled`가 false면 요청하지 않는다(섹션을 열기 전).
 *
 * 구서버(엔드포인트 미배포)는 404 → `notReady`로 안내한다.
 */
export const buildRegionStatUrl = (from: string, to: string): string =>
  `admin/fevent/stats/region?from=${encodeURIComponent(
    kstDayStartISO(from) ?? '',
  )}&to=${encodeURIComponent(kstDayEndISO(to) ?? '')}`;

const EMPTY_ROWS: BugoRegionStatRow[] = [];

//* 응답 모양 방어 — 제네릭 목록(배열)이 오거나 rows가 없으면 빈 결과로 본다
const normalize = (data: unknown): BugoRegionStatResponse => {
  const body = (data ?? {}) as Partial<BugoRegionStatResponse>;
  const rows = Array.isArray(body.rows) ? body.rows : EMPTY_ROWS;
  return {
    from: body.from ?? '',
    to: body.to ?? '',
    rows,
    feventCount:
      typeof body.feventCount === 'number'
        ? body.feventCount
        : rows.reduce((sum, row) => sum + (row.feventCount ?? 0), 0),
    unresolvedFeventCount:
      typeof body.unresolvedFeventCount === 'number' ? body.unresolvedFeventCount : 0,
  };
};

export const useAdminBugoRegionStat = (from: string, to: string, enabled: boolean) => {
  const valid = !!from && !!to && from <= to;
  const key = `adminBugoRegionStat|${from}|${to}`;

  const { data, loading, error, notReady, refetch } =
    useCachedResource<BugoRegionStatResponse>(
      key,
      async () => {
        const { data: body } = await api_.get<BugoRegionStatResponse>(
          buildRegionStatUrl(from, to),
        );
        return normalize(body);
      },
      { enabled: enabled && valid, errorMessage: '지역별 통계를 불러오지 못했습니다.' },
    );

  return {
    status: notReady
      ? ('notReady' as const)
      : error
      ? ('error' as const)
      : loading
      ? ('loading' as const)
      : data
      ? ('success' as const)
      : ('idle' as const),
    rows: data?.rows ?? EMPTY_ROWS,
    unresolvedFeventCount: data?.unresolvedFeventCount ?? 0,
    errorMessage: error,
    refetch,
  };
};
